import React, { useState, useEffect, useRef, useMemo } from 'react';
import { outboundColumns } from './outboundColumns';
import { CALL_OUTCOMES } from './constants';
import { Table } from 'antd';
import Button from '../../../components/Button'
import OutboundSlideOut from './OutboundSlideOut';
import './voiceOutbound.scss';
import axios from 'axios';
import PropTypes from 'prop-types';
import VoiceSubheader from '../VoiceSubheader';
import {
  SearchFilter,
  DobFilter,
  DateRangeFilter,
  WorkflowFilter,
  CallOutcomeFilter,
} from '../Filters';
import { withRouter } from 'react-router-dom';
import moment from 'moment';

const VoiceOutbound = ({
  reset,
  setReset,
  history,
}) => {
  const [query, setQuery] = useState({});
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [selectedWorkflows, setSelectedWorkflows] = useState([]);
  const [selectedCallOutcomes, setSelectedCallOutcomes] = useState([]);
  const [dob, setDob] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [slideOutCallIndex, setSlideOutCallIndex] = useState(null);
  const [deepLinkedRecord, setDeepLinkedRecord] = useState(null);
  const [isNavigationLoading, setIsNavigationLoading] = useState(false);
  const [inboundDeliveryOutcomesEnabled, setInboundDeliveryOutcomesEnabled] = useState(false);
  // Dark-by-default while the capability response loads: assume the inbound
  // outcomes are gated (enabled starts false) so their filter options stay
  // hidden on first paint. The index response overrides this with the
  // authoritative list.
  const [inboundDeliveryOutcomes, setInboundDeliveryOutcomes] = useState([
    CALL_OUTCOMES.RESULT_DELIVERED_VIA_INBOUND,
    CALL_OUTCOMES.INBOUND_RESULT_DELIVERY_UNVERIFIED,
  ]);
  const subheaderRef = useRef(null);
  const hasInitialized = useRef(false);
  const navigationAbortRef = useRef(null);
  const deepLinkAbortRef = useRef(null);
  const deepLinkRequestSeq = useRef(0);

  const record = useMemo(() => {
    if (slideOutCallIndex !== null && data[slideOutCallIndex]) return data[slideOutCallIndex];
    return deepLinkedRecord;
  }, [data, slideOutCallIndex, deepLinkedRecord]);

  // Derive slideOutCallId from record (no separate state needed)
  const slideOutCallId = useMemo(() => {
    if (!record) return null;
    return record.id;
  }, [record]);

  const getTotalPages = () => {
    const { total, pageSize } = pagination;
    if (!total || !pageSize) return 1;
    return Math.ceil(total / pageSize);
  };

  // Sync slideOutCallId with URL (skip on initial mount to avoid overwriting URL)
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }

    const currentParams = new URLSearchParams(history.location.search);

    if (slideOutCallId !== null) {
      const callId = currentParams.get('callId');
      if (callId !== String(slideOutCallId)) {
        currentParams.set('callId', slideOutCallId);
        history.replace(`?${currentParams.toString()}`);
      }
    } else {
      // Remove callId from URL when slideout is closed
      if (currentParams.has('callId')) {
        currentParams.delete('callId');
        history.replace(`?${currentParams.toString()}`);
      }
    }
  }, [slideOutCallId]);

  useEffect(() => {
    const currentParams = new URLSearchParams(history.location.search);
    const params = Object.fromEntries(currentParams.entries());
    params.workflows = params.workflows ? params.workflows.split(',') : [];
    params.outcomes = params.outcomes ? params.outcomes.split(',') : [];

    const search = params.search;
    const page = params.page;
    const per_page = params.per_page;
    const dob = params.dob;
    const start_date = params.start_date;
    const end_date = params.end_date;
    const workflows = params.workflows || [];
    const outcomes = params.outcomes || [];

    setSearch(search || '');
    setDateRange([
      start_date ? moment(start_date, 'YYYY-MM-DD') : null,
      end_date ? moment(end_date, 'YYYY-MM-DD') : null
    ]);
    setSelectedWorkflows(workflows);
    setSelectedCallOutcomes(outcomes);
    setDob(dob || null);
    setPagination({
      ...pagination,
      current: page || 1,
      pageSize: per_page || 10,
    });
    getOutboundCalls(params);
    setQuery(params);
  }, []);

  // Restore slideOutCallId from URL after data loads
  useEffect(() => {
    const currentParams = new URLSearchParams(history.location.search);
    const urlCallId = currentParams.get('callId');

    if (urlCallId && data.length > 0 && slideOutCallIndex === null) {
      const foundIndex = data.findIndex(call => String(call.id) === urlCallId);
      if (foundIndex !== -1) {
        setSlideOutCallIndex(foundIndex);
      } else {
        deepLinkAbortRef.current?.abort();
        const abortController = new AbortController();
        deepLinkAbortRef.current = abortController;
        deepLinkRequestSeq.current += 1;
        const requestSeq = deepLinkRequestSeq.current;

        axios({
          method: 'GET',
          url: `/api/v1/outbound_calls/${urlCallId}`,
          signal: abortController.signal,
        }).then((response) => {
          const latestCallId = new URLSearchParams(history.location.search).get('callId');
          if (requestSeq !== deepLinkRequestSeq.current || latestCallId !== urlCallId) return;

          setDeepLinkedRecord(response.data.data);
        }).catch((error) => {
          if (!axios.isCancel(error)) {
            // The scoped endpoint returns 404 when the request is unavailable.
          }
        });
      }
    }
  }, [data]);

  useEffect(() => () => {
    deepLinkRequestSeq.current += 1;
    deepLinkAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (reset) {
      handleClear();
      setReset(false);
    }
  }, [reset]);

  const getOutboundCalls = (newParams = {}, options = {}) => {
    const { skipTableLoading = false, skipCallId = false, signal } = options;

    if (!skipTableLoading) {
      setLoading(true);
    }

    // Get type and callId from current URL, not from newParams
    const currentParams = new URLSearchParams(history.location.search);
    const type = currentParams.get('type');
    const callId = currentParams.get('callId');

    // Remove type and callId from newParams if they exist
    delete newParams.type;
    delete newParams.callId;

    // Filter out empty arrays and empty strings before sending to API
    const cleanedParams = Object.entries(newParams).reduce((acc, [key, value]) => {
      if (value === null || value === undefined || value === '') {
        return acc;
      }
      if (Array.isArray(value) && value.length === 0) {
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {});

    return axios({
      method: 'GET',
      url: '/api/v1/outbound_calls',
      params: {
        ...cleanedParams,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
      signal,
    })
      .then((response) => {
        const calls = response.data.data || [];

        setInboundDeliveryOutcomesEnabled(!!response.data.inbound_delivery_outcomes_enabled);
        setInboundDeliveryOutcomes(response.data.inbound_delivery_outcomes || []);

        setData(calls);
        setPagination({
          current: response.data.page || 1,
          pageSize: response.data.per_page || 10,
          total: response.data.total || pagination.total,
        });

        // Build params object, excluding empty values
        const urlParams = {
          ...newParams,
          page: response.data.page || 1,
          per_page: response.data.per_page || 10,
        };

        // Remove empty arrays and empty strings
        Object.keys(urlParams).forEach(key => {
          const value = urlParams[key];
          if (value === null || value === undefined || value === '' ||
              (Array.isArray(value) && value.length === 0)) {
            delete urlParams[key];
          }
        });

        const query = new URLSearchParams(urlParams);
        if (type) {
          query.set('type', type);
        }
        if (callId && !skipCallId) {
          query.set('callId', callId);
        }
        history.push(`?${query.toString()}`);

        return calls;
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error('Failed to fetch outbound calls:', error);
        }
      })
      .finally(() => {
        if (!skipTableLoading) {
          setLoading(false);
        }
      });
  };

  const handleClear = () => {
    navigationAbortRef.current?.abort();
    deepLinkRequestSeq.current += 1;
    deepLinkAbortRef.current?.abort();
    setSlideOutCallIndex(null);
    setDeepLinkedRecord(null);
    // Strip callId from URL before getOutboundCalls reads it
    const currentParams = new URLSearchParams(history.location.search);
    if (currentParams.has('callId')) {
      currentParams.delete('callId');
      history.replace(`?${currentParams.toString()}`);
    }
    setSearch('');
    setDateRange(null);
    setDob(null);
    setSelectedWorkflows([]);
    setSelectedCallOutcomes([]);
    setQuery({ page: 1, per_page: pagination.pageSize });
    setPagination({
      ...pagination,
      current: 1,
      pageSize: pagination.pageSize,
    });
    getOutboundCalls({ page: 1, per_page: pagination.pageSize });
  };

  const handleTableChange = (newPagination) => {
    getOutboundCalls({ ...query, page: newPagination.current, per_page: newPagination.pageSize });
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const updateQuery = async (rawQuery = {}) => {
    let params = { ...query };
    Object.entries(rawQuery).forEach(([key, value]) => {
      if (value === null || value.length === 0) {
        delete params[key];
      } else {
        params[key] = value;
      }
    });
    params.page = 1;
    params.per_page = pagination.pageSize;
    setQuery(params);
    await getOutboundCalls(params);
  };

  const handleNavigate = (direction) => {
    if (slideOutCallIndex === null || !data || isNavigationLoading) return;

    const isPrevious = direction === 'previous';
    const currentPage = pagination.current;
    const totalPages = getTotalPages();

    // Try to move within current page
    const canMoveWithinPage = isPrevious
      ? slideOutCallIndex > 0
      : slideOutCallIndex < data.length - 1;

    if (canMoveWithinPage) {
      setSlideOutCallIndex(slideOutCallIndex + (isPrevious ? -1 : 1));
      return;
    }

    // Check if cross-page navigation is possible
    const canCrossPage = isPrevious
      ? currentPage > 1
      : currentPage < totalPages;

    if (canCrossPage) {
      setIsNavigationLoading(true);

      // Abort any in-flight cross-page navigation request
      navigationAbortRef.current?.abort();
      const abortController = new AbortController();
      navigationAbortRef.current = abortController;

      const targetPage = currentPage + (isPrevious ? -1 : 1);
      const newQuery = { ...query, page: targetPage, per_page: pagination.pageSize };
      setQuery(newQuery);

      getOutboundCalls(newQuery, {
        skipTableLoading: true,
        skipCallId: true,
        signal: abortController.signal,
      })
        .then((calls) => {
          if (!calls) return; // Request was aborted or failed — keep current state
          if (calls.length > 0) {
            const targetIndex = isPrevious ? calls.length - 1 : 0;
            setSlideOutCallIndex(targetIndex);
            // Explicitly set callId in URL since history.push already ran without it
            const currentParams = new URLSearchParams(history.location.search);
            currentParams.set('callId', calls[targetIndex].id);
            history.replace(`?${currentParams.toString()}`);
          } else {
            // Empty page (e.g., concurrent deletion) - close slideout
            setSlideOutCallIndex(null);
          }
        })
        .catch(() => {
          // Error already logged in getOutboundCalls
        })
        .finally(() => {
          setIsNavigationLoading(false);
        });
    }
  };

  const isNavigationDisabled = (direction) => {
    if (slideOutCallIndex === null || isNavigationLoading || !data || !data.length) {
      return true;
    }

    const currentPage = pagination.current;
    const totalPages = getTotalPages();

    if (direction === 'previous') {
      return slideOutCallIndex === 0 && currentPage <= 1;
    }
    if (direction === 'next') {
      return slideOutCallIndex >= data.length - 1 && currentPage >= totalPages;
    }
    return false;
  };

  return (
    <div className='voice-outbound'>
      <VoiceSubheader ref={subheaderRef}>
        <SearchFilter updateQuery={updateQuery} search={search} setSearch={setSearch} data={data} nameField="patient_name" placeholder="Search Calls (Name, Phone Number, MRN)" />
        <DobFilter updateQuery={updateQuery} dob={dob} setDob={setDob} />
        <DateRangeFilter updateQuery={updateQuery} dateRange={dateRange} setDateRange={setDateRange} />
        <WorkflowFilter updateQuery={updateQuery} selectedWorkflows={selectedWorkflows} setSelectedWorkflows={setSelectedWorkflows} />
        <CallOutcomeFilter updateQuery={updateQuery} selectedCallOutcomes={selectedCallOutcomes} setSelectedCallOutcomes={setSelectedCallOutcomes} inboundDeliveryOutcomesEnabled={inboundDeliveryOutcomesEnabled} inboundDeliveryOutcomes={inboundDeliveryOutcomes} />
        <div className='clear-button'>
          <Button
            type='primary'
            size='xxsmall'
            label='Clear'
            onClick={handleClear}
          />
        </div>
      </VoiceSubheader>
      <Table
        columns={outboundColumns}
        dataSource={data}
        rowKey={(record) => record.attempt_id}
        loading={loading}
        sticky={{
          offsetHeader: subheaderRef.current?.clientHeight,
        }}
        rowClassName={(_, index) => (index === slideOutCallIndex ? 'call-row-active' : '')}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          pageSizeOptions: [10, 20, 50],
          showSizeChanger: true,
          showTotal: (total, range) => `Viewing ${range[0]}-${range[1]} of ${total} calls`,
          position: ['bottomCenter'],
        }}
        onChange={(pagination) => handleTableChange(pagination)}
        onRow={(_, index) => ({
          onClick: () => {
            const selectedText = window.getSelection().toString();
            if (selectedText) return;
            navigationAbortRef.current?.abort();
            setDeepLinkedRecord(null);
            setSlideOutCallIndex(index);
          },
        })}
      />
      <OutboundSlideOut
        open={!!record}
        onClose={() => {
          navigationAbortRef.current?.abort();
          deepLinkRequestSeq.current += 1;
          deepLinkAbortRef.current?.abort();
          setSlideOutCallIndex(null);
          setDeepLinkedRecord(null);
        }}
        record={record}
        setCallIndex={handleNavigate}
        disableNext={isNavigationDisabled('next')}
        disablePrevious={isNavigationDisabled('previous')}
        isNavigationLoading={isNavigationLoading}
      />
    </div>
  );
};

VoiceOutbound.defaultProps = {
  reset: false,
  setReset: () => {},
  history: {},
};

VoiceOutbound.propTypes = {
  reset: PropTypes.bool,
  setReset: PropTypes.func,
  history: PropTypes.object,
};

export default withRouter(VoiceOutbound);
